import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckSquare, 
  Plus, 
  Search,
  Calendar,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  Circle,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tasks = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');

  const statusConfig = {
    todo: { label: isRTL ? 'للتنفيذ' : 'To Do', color: 'bg-slate-100 text-slate-700', icon: Circle },
    in_progress: { label: isRTL ? 'قيد التنفيذ' : 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
    review: { label: isRTL ? 'مراجعة' : 'Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
    completed: { label: isRTL ? 'مكتمل' : 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 }
  };

  const priorityConfig = {
    low: { label: isRTL ? 'منخفضة' : 'Low', color: 'border-l-gray-400' },
    medium: { label: isRTL ? 'متوسطة' : 'Medium', color: 'border-l-blue-400' },
    high: { label: isRTL ? 'عالية' : 'High', color: 'border-l-orange-400' },
    urgent: { label: isRTL ? 'عاجلة' : 'Urgent', color: 'border-l-red-400' }
  };

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pm/tasks`);
      setTasks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pm/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingTask) {
        await axios.put(`${API_URL}/api/pm/tasks/${editingTask.id}`, formData);
        toast.success(isRTL ? 'تم تحديث المهمة' : 'Task updated');
      } else {
        await axios.post(`${API_URL}/api/pm/tasks`, formData);
        toast.success(isRTL ? 'تم إنشاء المهمة' : 'Task created');
      }
      setShowDialog(false);
      setEditingTask(null);
      setFormData({});
      fetchTasks();
    } catch (err) {
      toast.error(isRTL ? 'فشل حفظ المهمة' : 'Failed to save task');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRTL ? 'حذف هذه المهمة؟' : 'Delete this task?')) {
      try {
        await axios.delete(`${API_URL}/api/pm/tasks/${id}`);
        toast.success(isRTL ? 'تم حذف المهمة' : 'Task deleted');
        fetchTasks();
      } catch (err) {
        toast.error(isRTL ? 'فشل الحذف' : 'Failed to delete');
      }
    }
  };

  const updateStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/pm/tasks/${taskId}`, { status: newStatus });
      toast.success(isRTL ? 'تم تحديث الحالة' : 'Status updated');
      fetchTasks();
    } catch (err) {
      toast.error(isRTL ? 'فشل التحديث' : 'Failed to update');
    }
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setFormData({ ...task, due_date: task.due_date?.split('T')[0] || '' });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingTask(null);
    setFormData({ status: 'todo', priority: 'medium' });
    setShowDialog(true);
  };

  const getProjectName = (projectId) => projects.find(p => p.id === projectId)?.name || (isRTL ? 'بدون مشروع' : 'No Project');

  const filteredTasks = tasks.filter(t => {
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesProject = filterProject === 'all' || t.project_id === filterProject;
    return matchesStatus && matchesProject;
  });

  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    review: filteredTasks.filter(t => t.status === 'review'),
    completed: filteredTasks.filter(t => t.status === 'completed')
  };

  const TaskCard = ({ task }) => {
    const StatusIcon = statusConfig[task.status]?.icon || Circle;
    return (
      <div className={`bg-card border rounded-lg p-4 border-l-4 ${priorityConfig[task.priority]?.color} hover:shadow-md transition-all group`}>
        <div className={`flex items-start justify-between mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <h4 className={`font-medium text-sm line-clamp-2 ${isRTL ? 'text-right' : ''}`}>{task.title}</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEdit(task)}>
                <Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}
              </DropdownMenuItem>
              {task.status !== 'completed' && (
                <DropdownMenuItem onClick={() => updateStatus(task.id, 'completed')}>
                  <CheckCircle2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'إكمال' : 'Complete'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(task.id)}>
                <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <p className={`text-xs text-muted-foreground mb-3 ${isRTL ? 'text-right' : ''}`}>{getProjectName(task.project_id)}</p>
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          {task.due_date && (
            <div className={`flex items-center gap-1 text-xs text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Calendar className="h-3 w-3" />
              {format(new Date(task.due_date), 'MMM d')}
            </div>
          )}
          {task.estimated_hours && (
            <div className={`flex items-center gap-1 text-xs text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Clock className="h-3 w-3" />
              {task.estimated_hours}{isRTL ? 'س' : 'h'}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.tasks.title')}</h1>
          <p className="text-muted-foreground">{t('pages.tasks.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {t('pages.tasks.addTask')}
        </Button>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-48"><SelectValue placeholder={isRTL ? 'جميع المشاريع' : 'All Projects'} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع المشاريع' : 'All Projects'}</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board */}
      <div className={`grid grid-cols-4 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
        {Object.entries(statusConfig).map(([status, config]) => (
          <div key={status} className="space-y-3">
            <div className={`flex items-center gap-2 px-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <config.icon className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">{config.label}</span>
              <Badge variant="secondary" className={isRTL ? 'mr-auto' : 'ml-auto'}>{tasksByStatus[status].length}</Badge>
            </div>
            <div className="space-y-2 min-h-[300px] bg-muted/30 rounded-lg p-2">
              {tasksByStatus[status].map(task => <TaskCard key={task.id} task={task} />)}
              {tasksByStatus[status].length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">{isRTL ? 'لا توجد مهام' : 'No tasks'}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-w-md ${isRTL ? 'text-right' : ''}`}>
          <DialogHeader><DialogTitle>{editingTask ? (isRTL ? 'تعديل المهمة' : 'Edit Task') : (isRTL ? 'مهمة جديدة' : 'New Task')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.tasks.taskName')} *</Label>
              <Input value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder={isRTL ? 'عنوان المهمة' : 'Task title'} className={isRTL ? 'text-right' : ''} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الوصف' : 'Description'}</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} className={isRTL ? 'text-right' : ''} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'المشروع' : 'Project'}</Label>
              <Select value={formData.project_id || ''} onValueChange={v => setFormData({...formData, project_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر المشروع' : 'Select project'} /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.tasks.status')}</Label>
                <Select value={formData.status || 'todo'} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pages.tasks.priority')}</Label>
                <Select value={formData.priority || 'medium'} onValueChange={v => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.tasks.dueDate')}</Label>
                <Input type="date" value={formData.due_date || ''} onChange={e => setFormData({...formData, due_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'الساعات المقدرة' : 'Est. Hours'}</Label>
                <Input type="number" value={formData.estimated_hours || ''} onChange={e => setFormData({...formData, estimated_hours: parseFloat(e.target.value) || 0})} className={isRTL ? 'text-right' : ''} />
              </div>
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{editingTask ? (isRTL ? 'تحديث' : 'Update') : (isRTL ? 'إنشاء' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;
